import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { API_URL } from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { toast } from 'sonner';
import {
  Users, Plus, Edit, Search, RefreshCw, User, Clock, UserCheck,
  ChevronDown, ChevronUp, Upload, UserX, UserPlus, MapPin,
  Calendar, TrendingUp, AlertTriangle, FileSpreadsheet, Filter,
  X, Eye, ArrowUpDown, BarChart3, CheckCircle, Download, XCircle
} from 'lucide-react';

const API = API_URL;

/* ========== HELPER: Sort Icon ========== */
function SortIcon({ field, sortField, sortDir }) {
  if (sortField !== field) return <ArrowUpDown size={12} className="text-slate-300 dark:text-slate-600" />;
  return sortDir === 'asc'
    ? <ChevronUp size={12} className="text-primary" />
    : <ChevronDown size={12} className="text-primary" />;
}

/* ========== MAIN COMPONENT ========== */
export default function EmployeesPage() {
  const { t, language } = useLanguage();
  const { user, token } = useAuth();
  const { effectiveTenantId, loading: tenantLoading } = useTenant();
  const fr = language === 'fr';
  const [searchParams] = useSearchParams();

  // Data
  const [employees, setEmployees] = useState([]);
  const [zones, setZones] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterZone, setFilterZone] = useState(searchParams.get('zone') || 'all');
  const [filterShift, setFilterShift] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterActive, setFilterActive] = useState('active');

  // Sorting
  const [sortField, setSortField] = useState('employeeCode');
  const [sortDir, setSortDir] = useState('asc');

  // Selection
  const [selectedIds, setSelectedIds] = useState([]);

  // Dialogs
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkValue, setBulkValue] = useState('');
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [importStep, setImportStep] = useState('upload'); // upload | preview | result
  const fileInputRef = useRef(null);

  // Employee detail panel
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailEmployee, setDetailEmployee] = useState(null);
  const [detailStats, setDetailStats] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Form
  const [formData, setFormData] = useState({
    employeeCode: '', firstName: '', lastName: '', jobTitle: '', zoneId: '', shiftId: ''
  });

  // Supervisor
  const isSupervisor = user?.role === 'tenant_supervisor';

  /* ========== FETCH ========== */
  const fetchData = useCallback(async () => {
    if (!effectiveTenantId) return;
    try {
      const [empRes, zonesRes, shiftsRes] = await Promise.all([
        axios.get(`${API}/employees?tenantId=${effectiveTenantId}`),
        axios.get(`${API}/zones?tenantId=${effectiveTenantId}`),
        axios.get(`${API}/shifts?tenantId=${effectiveTenantId}`)
      ]);
      setEmployees(empRes.data);
      setZones(zonesRes.data);
      setShifts(shiftsRes.data);
    } catch (error) {
      console.error('Failed to fetch:', error);
      toast.error(fr ? 'Erreur de chargement' : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [effectiveTenantId, fr]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ========== FILTERED & SORTED ========== */
  const filteredEmployees = useMemo(() => {
    let result = [...employees];

    // Active filter
    if (filterActive === 'active') result = result.filter(e => e.active !== false);
    else if (filterActive === 'inactive') result = result.filter(e => e.active === false);

    // Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(e =>
        e.firstName?.toLowerCase().includes(q) ||
        e.lastName?.toLowerCase().includes(q) ||
        e.employeeCode?.includes(q) ||
        e.jobTitle?.toLowerCase().includes(q)
      );
    }

    // Zone
    if (filterZone !== 'all') {
      result = result.filter(e => e.zoneId === filterZone);
    }

    // Shift
    if (filterShift !== 'all') {
      if (filterShift === 'none') result = result.filter(e => !e.shiftId);
      else result = result.filter(e => e.shiftId === filterShift);
    }

    // Status
    if (filterStatus !== 'all') {
      result = result.filter(e => e.currentStatus === filterStatus);
    }

    // Sort
    result.sort((a, b) => {
      let va = a[sortField] || '';
      let vb = b[sortField] || '';
      if (sortField === 'name') {
        va = `${a.lastName} ${a.firstName}`.toLowerCase();
        vb = `${b.lastName} ${b.firstName}`.toLowerCase();
      }
      if (typeof va === 'string') {
        const cmp = va.localeCompare(vb);
        return sortDir === 'asc' ? cmp : -cmp;
      }
      return sortDir === 'asc' ? va - vb : vb - va;
    });

    return result;
  }, [employees, searchQuery, filterZone, filterShift, filterStatus, filterActive, sortField, sortDir]);

  /* ========== SORT ========== */
  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  /* ========== SELECTION ========== */
  const isAllSelected = filteredEmployees.length > 0 && selectedIds.length === filteredEmployees.length;
  const toggleSelectAll = () => {
    setSelectedIds(isAllSelected ? [] : filteredEmployees.map(e => e.id));
  };
  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  /* ========== HELPERS ========== */
  const getZoneName = (zoneId) => zones.find(z => z.id === zoneId)?.name || '-';
  const getShiftName = (shiftId) => shifts.find(s => s.id === shiftId)?.name || '-';

  const getStatusBadge = (status, active) => {
    if (active === false) return <Badge className="bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-[10px]">{fr ? 'Inactif' : 'Inactive'}</Badge>;
    const cfg = {
      IN: { label: fr ? 'Présent' : 'In', cls: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400' },
      BREAK: { label: fr ? 'Pause' : 'Break', cls: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400' },
      OUT: { label: fr ? 'Sorti' : 'Out', cls: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300' },
    };
    const c = cfg[status] || cfg.OUT;
    return <Badge className={`${c.cls} text-[10px]`}>{c.label}</Badge>;
  };

  // Unique job titles for filter
  const jobTitles = useMemo(() => {
    const set = new Set(employees.map(e => e.jobTitle).filter(Boolean));
    return Array.from(set).sort();
  }, [employees]);

  /* ========== FORM ========== */
  const resetForm = () => {
    setFormData({ employeeCode: '', firstName: '', lastName: '', jobTitle: '', zoneId: '', shiftId: '' });
    setEditingEmployee(null);
  };
  const openAddDialog = () => { resetForm(); setDialogOpen(true); };
  const openEditDialog = (emp) => {
    setEditingEmployee(emp);
    setFormData({
      employeeCode: emp.employeeCode,
      firstName: emp.firstName,
      lastName: emp.lastName,
      jobTitle: emp.jobTitle || '',
      zoneId: emp.zoneId || '',
      shiftId: emp.shiftId || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!/^\d{5}$/.test(formData.employeeCode)) { toast.error(fr ? 'Code invalide (5 chiffres)' : 'Invalid code (5 digits)'); return; }
    if (!effectiveTenantId) { toast.error(fr ? 'Tenant non sélectionné' : 'Tenant not selected'); return; }
    try {
      if (editingEmployee) {
        await axios.put(`${API}/employees/${editingEmployee.id}`, {
          firstName: formData.firstName, lastName: formData.lastName,
          jobTitle: formData.jobTitle, zoneId: formData.zoneId || null, shiftId: formData.shiftId || null
        });
        toast.success(fr ? 'Employé modifié' : 'Employee updated');
      } else {
        await axios.post(`${API}/employees`, {
          tenantId: effectiveTenantId, employeeCode: formData.employeeCode,
          firstName: formData.firstName, lastName: formData.lastName,
          jobTitle: formData.jobTitle, zoneId: formData.zoneId || null, shiftId: formData.shiftId || null
        });
        toast.success(fr ? 'Employé créé' : 'Employee created');
      }
      setDialogOpen(false); resetForm(); fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : fr ? 'Erreur' : 'Error');
    }
  };

  /* ========== DEACTIVATE ========== */
  const handleDeactivate = async () => {
    if (!editingEmployee) return;
    try {
      await axios.put(`${API}/employees/${editingEmployee.id}`, { active: false });
      toast.success(fr ? 'Employé désactivé' : 'Employee deactivated');
      setDeactivateDialogOpen(false); setEditingEmployee(null); fetchData();
    } catch { toast.error(fr ? 'Erreur' : 'Error'); }
  };

  const handleActivate = async (emp) => {
    try {
      await axios.put(`${API}/employees/${emp.id}`, { active: true });
      toast.success(fr ? 'Employé réactivé' : 'Employee reactivated');
      fetchData();
    } catch { toast.error(fr ? 'Erreur' : 'Error'); }
  };

  /* ========== BULK ACTIONS ========== */
  const openBulkAction = (action) => { setBulkAction(action); setBulkValue(''); setBulkActionDialogOpen(true); };

  const handleBulkAction = async () => {
    if (selectedIds.length === 0) return;
    try {
      await axios.post(`${API}/employees/bulk-action`, {
        action: bulkAction,
        employeeIds: selectedIds,
        value: bulkValue === 'none' ? null : bulkValue,
        tenantId: effectiveTenantId
      });
      const labels = { assign_zone: fr ? 'Zone assignée' : 'Zone assigned', assign_shift: fr ? 'Quart assigné' : 'Shift assigned', deactivate: fr ? 'Employés désactivés' : 'Employees deactivated' };
      toast.success(`${labels[bulkAction] || bulkAction} (${selectedIds.length})`);
      setBulkActionDialogOpen(false); setSelectedIds([]); setBulkAction(''); fetchData();
    } catch { toast.error(fr ? 'Erreur' : 'Error'); }
  };

  /* ========== IMPORT ========== */
  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/employees/import-template?tenantId=${effectiveTenantId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'modele_import_employes.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error(fr ? 'Session expirée. Veuillez vous reconnecter.' : 'Session expired. Please log in again.');
      } else {
        toast.error(fr ? 'Erreur lors du téléchargement du modèle' : 'Error downloading template');
      }
    }
  };

  const handlePreview = async () => {
    if (!importFile) return;
    setPreviewLoading(true);
    setImportPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const res = await axios.post(`${API}/employees/import-preview?tenantId=${effectiveTenantId}`, fd);
      setImportPreview(res.data);
      setImportStep('preview');
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error(fr ? 'Votre session n\'est pas reconnue pour l\'import. Veuillez vous reconnecter.' : 'Session not recognized for import. Please log in again.');
      } else {
        toast.error(err.response?.data?.detail || (fr ? 'Erreur de prévisualisation' : 'Preview error'));
      }
    } finally { setPreviewLoading(false); }
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const res = await axios.post(`${API}/employees/import?tenantId=${effectiveTenantId}`, fd);
      setImportResult(res.data);
      setImportStep('result');
      if (res.data.created > 0) fetchData();
      toast.success(fr ? `${res.data.created} employé(s) importé(s)` : `${res.data.created} employee(s) imported`);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error(fr ? 'Votre session n\'est pas reconnue pour l\'import. Veuillez vous reconnecter.' : 'Session not recognized for import. Please log in again.');
      } else {
        toast.error(err.response?.data?.detail || (fr ? 'Erreur d\'import' : 'Import error'));
      }
    } finally { setImporting(false); }
  };

  const resetImportDialog = () => {
    setImportFile(null);
    setImportPreview(null);
    setImportResult(null);
    setImportStep('upload');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  /* ========== EMPLOYEE DETAIL ========== */
  const openDetail = async (emp) => {
    setDetailEmployee(emp); setDetailOpen(true); setDetailLoading(true); setDetailStats(null);
    try {
      const res = await axios.get(`${API}/employees/${emp.id}/detail-stats?tenantId=${effectiveTenantId}&days=30`);
      setDetailStats(res.data);
    } catch (err) {
      console.error(err);
    } finally { setDetailLoading(false); }
  };

  /* ========== KPIs ========== */
  const kpis = useMemo(() => {
    const active = employees.filter(e => e.active !== false);
    const inactive = employees.filter(e => e.active === false);
    const present = active.filter(e => e.currentStatus === 'IN');
    const onBreak = active.filter(e => e.currentStatus === 'BREAK');
    return { total: employees.length, active: active.length, inactive: inactive.length, present: present.length, onBreak: onBreak.length };
  }, [employees]);

  const hasActiveFilters = searchQuery || filterZone !== 'all' || filterShift !== 'all' || filterStatus !== 'all';

  const clearFilters = () => {
    setSearchQuery(''); setFilterZone('all'); setFilterShift('all'); setFilterStatus('all');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><RefreshCw className="animate-spin text-primary" size={32} /></div>;
  }

  return (
    <div className="space-y-4" data-testid="employees-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 dark:text-white">{fr ? 'Gestion employés' : 'Employee Management'}</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {filteredEmployees.length}/{employees.length} {fr ? 'employés' : 'employees'}
            {selectedIds.length > 0 && <span className="ml-2 text-primary font-semibold">({selectedIds.length} {fr ? 'sélectionné(s)' : 'selected'})</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!isSupervisor && (
            <>
              <Button variant="outline" size="sm" className="gap-1.5 h-8" onClick={() => { setImportFile(null); setImportResult(null); setImportDialogOpen(true); }} data-testid="import-btn">
                <Upload size={14} /> {fr ? 'Importer' : 'Import'}
              </Button>
              <Button size="sm" className="gap-1.5 h-8" onClick={openAddDialog} data-testid="add-employee-btn">
                <Plus size={14} /> {fr ? 'Ajouter' : 'Add'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="kpi-strip">
        <div className="flex items-center gap-3 p-3 rounded-lg border dark:border-slate-700 bg-blue-50/50 dark:bg-blue-950/20" data-testid="kpi-total">
          <Users size={18} className="text-blue-600 dark:text-blue-400" />
          <div><p className="text-lg font-bold text-slate-800 dark:text-white">{kpis.total}</p><p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">{fr ? 'Total' : 'Total'}</p></div>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg border dark:border-slate-700 bg-green-50/50 dark:bg-green-950/20" data-testid="kpi-active">
          <UserCheck size={18} className="text-green-600 dark:text-green-400" />
          <div><p className="text-lg font-bold text-slate-800 dark:text-white">{kpis.active}</p><p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">{fr ? 'Actifs' : 'Active'}</p></div>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg border dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/20" data-testid="kpi-inactive">
          <UserX size={18} className="text-slate-600 dark:text-slate-400" />
          <div><p className="text-lg font-bold text-slate-800 dark:text-white">{kpis.inactive}</p><p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">{fr ? 'Inactifs' : 'Inactive'}</p></div>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg border dark:border-slate-700 bg-emerald-50/50 dark:bg-emerald-950/20" data-testid="kpi-present">
          <CheckCircle size={18} className="text-emerald-600 dark:text-emerald-400" />
          <div><p className="text-lg font-bold text-slate-800 dark:text-white">{kpis.present}</p><p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">{fr ? 'Présents' : 'Present'}</p></div>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg border dark:border-slate-700 bg-amber-50/50 dark:bg-amber-950/20" data-testid="kpi-break">
          <Clock size={18} className="text-amber-600 dark:text-amber-400" />
          <div><p className="text-lg font-bold text-slate-800 dark:text-white">{kpis.onBreak}</p><p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">{fr ? 'En pause' : 'On Break'}</p></div>
        </div>
      </div>

      {/* Filters Bar */}
      <Card className="dark:border-slate-700" data-testid="filters-bar">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-2">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder={fr ? 'Rechercher nom, code, poste...' : 'Search name, code, job...'}
                className="pl-8 h-8 text-sm"
                data-testid="search-input"
              />
            </div>

            {/* Active/Inactive toggle */}
            <Select value={filterActive} onValueChange={setFilterActive}>
              <SelectTrigger className="h-8 w-28 text-xs" data-testid="filter-active">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{fr ? 'Tous' : 'All'}</SelectItem>
                <SelectItem value="active">{fr ? 'Actifs' : 'Active'}</SelectItem>
                <SelectItem value="inactive">{fr ? 'Inactifs' : 'Inactive'}</SelectItem>
              </SelectContent>
            </Select>

            {/* Zone */}
            <Select value={filterZone} onValueChange={setFilterZone}>
              <SelectTrigger className="h-8 w-36 text-xs" data-testid="filter-zone">
                <MapPin size={12} className="mr-1" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{fr ? 'Toutes zones' : 'All zones'}</SelectItem>
                {zones.map(z => <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>)}
              </SelectContent>
            </Select>

            {/* Shift */}
            <Select value={filterShift} onValueChange={setFilterShift}>
              <SelectTrigger className="h-8 w-36 text-xs" data-testid="filter-shift">
                <Clock size={12} className="mr-1" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{fr ? 'Tous quarts' : 'All shifts'}</SelectItem>
                <SelectItem value="none">{fr ? 'Non assigné' : 'Unassigned'}</SelectItem>
                {shifts.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>

            {/* Status */}
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="h-8 w-28 text-xs" data-testid="filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{fr ? 'Tous statuts' : 'All statuses'}</SelectItem>
                <SelectItem value="IN">{fr ? 'Présent' : 'In'}</SelectItem>
                <SelectItem value="BREAK">{fr ? 'Pause' : 'Break'}</SelectItem>
                <SelectItem value="OUT">{fr ? 'Sorti' : 'Out'}</SelectItem>
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" className="h-8 text-xs gap-1 text-slate-500" onClick={clearFilters} data-testid="clear-filters-btn">
                <X size={12} /> {fr ? 'Réinitialiser' : 'Clear'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions Bar */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-2 p-3 bg-primary/5 dark:bg-primary/10 rounded-lg border border-primary/20" data-testid="bulk-actions-bar">
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
            {selectedIds.length} {fr ? 'sélectionné(s)' : 'selected'}
          </span>
          <div className="flex-1" />
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => openBulkAction('assign_zone')} data-testid="bulk-assign-zone-btn">
            <MapPin size={12} /> {fr ? 'Assigner zone' : 'Assign Zone'}
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => openBulkAction('assign_shift')} data-testid="bulk-assign-shift-btn">
            <Clock size={12} /> {fr ? 'Assigner quart' : 'Assign Shift'}
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20" onClick={() => openBulkAction('deactivate')} data-testid="bulk-deactivate-btn">
            <UserX size={12} /> {fr ? 'Désactiver' : 'Deactivate'}
          </Button>
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setSelectedIds([])} data-testid="clear-selection-btn">
            <X size={12} />
          </Button>
        </div>
      )}

      {/* Table */}
      <Card className="dark:border-slate-700" data-testid="employees-table-card">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80 dark:bg-slate-800/80">
                  <TableHead className="w-10 py-2">
                    <Checkbox checked={isAllSelected} onCheckedChange={toggleSelectAll} data-testid="select-all-checkbox" />
                  </TableHead>
                  {[
                    { key: 'employeeCode', label: fr ? 'Code' : 'Code', width: 'w-20' },
                    { key: 'name', label: fr ? 'Nom' : 'Name', width: '' },
                    { key: 'jobTitle', label: fr ? 'Poste' : 'Position', width: '' },
                    { key: 'zoneId', label: fr ? 'Zone' : 'Zone', width: '' },
                    { key: 'shiftId', label: fr ? 'Quart' : 'Shift', width: '' },
                    { key: 'currentStatus', label: fr ? 'Statut' : 'Status', width: 'w-24' },
                  ].map(col => (
                    <TableHead key={col.key} className={`py-2 ${col.width}`}>
                      <button onClick={() => handleSort(col.key)} className="flex items-center gap-1 text-xs font-bold" data-testid={`sort-${col.key}`}>
                        {col.label} <SortIcon field={col.key} sortField={sortField} sortDir={sortDir} />
                      </button>
                    </TableHead>
                  ))}
                  <TableHead className="py-2 w-28 text-right text-xs font-bold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEmployees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-slate-500 dark:text-slate-400 py-12">
                      {fr ? 'Aucun employé trouvé' : 'No employees found'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEmployees.map((emp, idx) => (
                    <TableRow
                      key={emp.id}
                      className={`transition-colors cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40 ${
                        selectedIds.includes(emp.id) ? 'bg-primary/5 dark:bg-primary/10' : ''
                      } ${idx % 2 === 1 ? 'bg-slate-25 dark:bg-slate-900/30' : ''} ${
                        emp.active === false ? 'opacity-60' : ''
                      }`}
                      data-testid={`employee-row-${emp.employeeCode}`}
                      onClick={() => openDetail(emp)}
                    >
                      <TableCell className="py-2" onClick={e => e.stopPropagation()}>
                        <Checkbox checked={selectedIds.includes(emp.id)} onCheckedChange={() => toggleSelect(emp.id)} data-testid={`select-${emp.employeeCode}`} />
                      </TableCell>
                      <TableCell className="py-2 font-mono font-bold text-sm text-slate-800 dark:text-slate-200">{emp.employeeCode}</TableCell>
                      <TableCell className="py-2">
                        <div>
                          <p className="font-medium text-sm text-slate-900 dark:text-slate-100">{emp.firstName} {emp.lastName}</p>
                        </div>
                      </TableCell>
                      <TableCell className="py-2 text-sm text-slate-600 dark:text-slate-400">{emp.jobTitle || '-'}</TableCell>
                      <TableCell className="py-2 text-sm text-slate-600 dark:text-slate-400">{getZoneName(emp.zoneId)}</TableCell>
                      <TableCell className="py-2">
                        {emp.shiftId ? (
                          <span className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400">
                            <Clock size={12} /> {getShiftName(emp.shiftId)}
                          </span>
                        ) : <span className="text-slate-400 dark:text-slate-600 text-xs italic">{fr ? 'Non assigné' : 'Unassigned'}</span>}
                      </TableCell>
                      <TableCell className="py-2">{getStatusBadge(emp.currentStatus, emp.active)}</TableCell>
                      <TableCell className="py-2 text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openDetail(emp)} data-testid={`view-${emp.employeeCode}`}>
                            <Eye size={14} className="text-slate-500" />
                          </Button>
                          {!isSupervisor && (
                            <>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEditDialog(emp)} data-testid={`edit-${emp.employeeCode}`}>
                                <Edit size={14} className="text-slate-500" />
                              </Button>
                              {emp.active !== false ? (
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20" onClick={() => { setEditingEmployee(emp); setDeactivateDialogOpen(true); }} data-testid={`deactivate-${emp.employeeCode}`}>
                                  <UserX size={14} />
                                </Button>
                              ) : (
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-green-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-950/20" onClick={() => handleActivate(emp)} data-testid={`activate-${emp.employeeCode}`}>
                                  <UserPlus size={14} />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* ========== ADD/EDIT DIALOG ========== */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <User size={18} />
              {editingEmployee ? (fr ? 'Modifier employé' : 'Edit Employee') : (fr ? 'Ajouter employé' : 'Add Employee')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{fr ? 'Code employé' : 'Employee Code'} *</Label>
                <Input value={formData.employeeCode} onChange={e => setFormData({ ...formData, employeeCode: e.target.value })} placeholder="12345" maxLength={5} disabled={!!editingEmployee} required data-testid="employee-code-input" />
                <p className="text-[10px] text-slate-500">{fr ? '5 chiffres requis' : '5 digits required'}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{fr ? 'Prénom' : 'First Name'} *</Label>
                  <Input value={formData.firstName} onChange={e => setFormData({ ...formData, firstName: e.target.value })} required data-testid="first-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>{fr ? 'Nom' : 'Last Name'} *</Label>
                  <Input value={formData.lastName} onChange={e => setFormData({ ...formData, lastName: e.target.value })} required data-testid="last-name-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{fr ? 'Poste' : 'Job Title'}</Label>
                <Input value={formData.jobTitle} onChange={e => setFormData({ ...formData, jobTitle: e.target.value })} data-testid="job-title-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{fr ? 'Zone' : 'Zone'}</Label>
                  <Select value={formData.zoneId || 'unassigned'} onValueChange={v => setFormData({ ...formData, zoneId: v === 'unassigned' ? '' : v })}>
                    <SelectTrigger data-testid="zone-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">{fr ? 'Non assigné' : 'Unassigned'}</SelectItem>
                      {zones.map(z => <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{fr ? 'Quart' : 'Shift'}</Label>
                  <Select value={formData.shiftId || 'unassigned'} onValueChange={v => setFormData({ ...formData, shiftId: v === 'unassigned' ? '' : v })}>
                    <SelectTrigger data-testid="shift-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">{fr ? 'Non assigné' : 'Unassigned'}</SelectItem>
                      {shifts.map(s => <SelectItem key={s.id} value={s.id}>{s.name} ({s.startTime}-{s.endTime})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{fr ? 'Annuler' : 'Cancel'}</Button>
              <Button type="submit" data-testid="save-employee-btn">{fr ? 'Sauvegarder' : 'Save'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ========== DEACTIVATE DIALOG ========== */}
      <Dialog open={deactivateDialogOpen} onOpenChange={setDeactivateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-amber-600 flex items-center gap-2">
              <UserX size={18} /> {fr ? 'Désactiver employé' : 'Deactivate Employee'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {fr
              ? `Voulez-vous désactiver ${editingEmployee?.firstName} ${editingEmployee?.lastName} (${editingEmployee?.employeeCode}) ? L'employé ne sera plus visible dans les listes actives.`
              : `Deactivate ${editingEmployee?.firstName} ${editingEmployee?.lastName} (${editingEmployee?.employeeCode})? They will be hidden from active lists.`
            }
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeactivateDialogOpen(false)}>{fr ? 'Annuler' : 'Cancel'}</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={handleDeactivate} data-testid="confirm-deactivate-btn">
              {fr ? 'Désactiver' : 'Deactivate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== BULK ACTION DIALOG ========== */}
      <Dialog open={bulkActionDialogOpen} onOpenChange={setBulkActionDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {bulkAction === 'assign_zone' && <><MapPin size={18} /> {fr ? 'Assigner zone' : 'Assign Zone'}</>}
              {bulkAction === 'assign_shift' && <><Clock size={18} /> {fr ? 'Assigner quart' : 'Assign Shift'}</>}
              {bulkAction === 'deactivate' && <><UserX size={18} className="text-red-600" /> {fr ? 'Désactiver' : 'Deactivate'}</>}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {selectedIds.length} {fr ? 'employé(s) sélectionné(s)' : 'employee(s) selected'}
            </p>

            {bulkAction === 'assign_zone' && (
              <Select value={bulkValue || 'none'} onValueChange={setBulkValue}>
                <SelectTrigger data-testid="bulk-zone-select"><SelectValue placeholder={fr ? 'Choisir zone' : 'Choose zone'} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{fr ? 'Retirer zone' : 'Remove zone'}</SelectItem>
                  {zones.map(z => <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}

            {bulkAction === 'assign_shift' && (
              <Select value={bulkValue || 'none'} onValueChange={setBulkValue}>
                <SelectTrigger data-testid="bulk-shift-select"><SelectValue placeholder={fr ? 'Choisir quart' : 'Choose shift'} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{fr ? 'Retirer quart' : 'Remove shift'}</SelectItem>
                  {shifts.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}

            {bulkAction === 'deactivate' && (
              <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded-lg text-sm text-red-700 dark:text-red-400">
                {fr ? 'Cette action désactivera les employés sélectionnés. Ils pourront être réactivés ultérieurement.' : 'This will deactivate selected employees. They can be reactivated later.'}
              </div>
            )}

            {/* Selected preview */}
            <div className="bg-slate-50 dark:bg-slate-800/40 rounded-lg p-3 max-h-32 overflow-y-auto">
              <p className="text-xs font-semibold text-slate-500 mb-1">{fr ? 'Employés:' : 'Employees:'}</p>
              {employees.filter(e => selectedIds.includes(e.id)).map(emp => (
                <p key={emp.id} className="text-xs text-slate-600 dark:text-slate-400">{emp.employeeCode} - {emp.firstName} {emp.lastName}</p>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkActionDialogOpen(false)}>{fr ? 'Annuler' : 'Cancel'}</Button>
            <Button
              onClick={handleBulkAction}
              className={bulkAction === 'deactivate' ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
              disabled={bulkAction !== 'deactivate' && !bulkValue}
              data-testid="confirm-bulk-action-btn"
            >
              {fr ? 'Confirmer' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== IMPORT DIALOG ========== */}
      <Dialog open={importDialogOpen} onOpenChange={(open) => { setImportDialogOpen(open); if (!open) resetImportDialog(); }}>
        <DialogContent className="sm:max-w-2xl dark:bg-slate-900 dark:border-slate-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <FileSpreadsheet size={18} />
              {fr ? 'Importer des employés' : 'Import Employees'}
              {importStep !== 'upload' && (
                <Badge variant="outline" className="text-[10px] ml-1">
                  {importStep === 'preview' ? (fr ? 'Prévisualisation' : 'Preview') : (fr ? 'Résultat' : 'Result')}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {/* Step 1: Upload */}
          {importStep === 'upload' && (
            <div className="py-3 space-y-4">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleDownloadTemplate} className="gap-1.5 text-xs" data-testid="download-template-btn">
                  <Download size={14} />
                  {fr ? 'Télécharger le modèle CSV' : 'Download CSV template'}
                </Button>
                <span className="text-xs text-slate-400">{fr ? '(pré-rempli avec vos zones, sites, quarts)' : '(pre-filled with your zones, sites, shifts)'}</span>
              </div>

              <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg text-xs text-blue-700 dark:text-blue-400 space-y-1.5">
                <p className="font-semibold">{fr ? 'Colonnes attendues :' : 'Expected columns:'}</p>
                <p className="font-mono text-[10px]">Code | Prénom | Nom | Email | Poste | Département | Site | Zone | Quart | Téléphone | Date embauche</p>
                <p>{fr ? 'Code (5 chiffres), Prénom et Nom sont obligatoires. Les autres colonnes sont optionnelles.' : 'Code (5 digits), First name and Last name are required. Other columns are optional.'}</p>
              </div>

              <div className="flex items-center gap-3">
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={e => setImportFile(e.target.files?.[0] || null)}
                  className="flex-1"
                  data-testid="import-file-input"
                />
              </div>
            </div>
          )}

          {/* Step 2: Preview */}
          {importStep === 'preview' && importPreview && (
            <div className="py-3 space-y-3">
              {/* Summary */}
              <div className="flex gap-2 flex-wrap">
                <Badge className="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 gap-1">
                  <CheckCircle size={10} />{importPreview.validCount} {fr ? 'valides' : 'valid'}
                </Badge>
                {importPreview.duplicateCount > 0 && (
                  <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 gap-1">
                    <AlertTriangle size={10} />{importPreview.duplicateCount} {fr ? 'doublons' : 'duplicates'}
                  </Badge>
                )}
                {importPreview.errorCount > 0 && (
                  <Badge className="bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 gap-1">
                    <XCircle size={10} />{importPreview.errorCount} {fr ? 'erreurs' : 'errors'}
                  </Badge>
                )}
                <span className="text-xs text-slate-400 self-center">/ {importPreview.totalRows} {fr ? 'lignes' : 'rows'}</span>
              </div>

              {/* Preview Table */}
              <div className="max-h-64 overflow-auto border rounded-md dark:border-slate-800">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0">
                    <tr>
                      <th className="text-left p-1.5 font-semibold text-slate-500">#</th>
                      <th className="text-left p-1.5 font-semibold text-slate-500">{fr ? 'Statut' : 'Status'}</th>
                      <th className="text-left p-1.5 font-semibold text-slate-500">Code</th>
                      <th className="text-left p-1.5 font-semibold text-slate-500">{fr ? 'Prénom' : 'First'}</th>
                      <th className="text-left p-1.5 font-semibold text-slate-500">{fr ? 'Nom' : 'Last'}</th>
                      <th className="text-left p-1.5 font-semibold text-slate-500">{fr ? 'Poste' : 'Job'}</th>
                      <th className="text-left p-1.5 font-semibold text-slate-500">Site</th>
                      <th className="text-left p-1.5 font-semibold text-slate-500">Zone</th>
                      <th className="text-left p-1.5 font-semibold text-slate-500">{fr ? 'Quart' : 'Shift'}</th>
                      <th className="text-left p-1.5 font-semibold text-slate-500">{fr ? 'Détails' : 'Details'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y dark:divide-slate-800">
                    {importPreview.rows.map((row, idx) => (
                      <tr key={idx} className={row.status === 'error' ? 'bg-red-50/50 dark:bg-red-950/10' : row.status === 'duplicate' ? 'bg-amber-50/50 dark:bg-amber-950/10' : ''} data-testid={`preview-row-${idx}`}>
                        <td className="p-1.5 text-slate-400">{row.row}</td>
                        <td className="p-1.5">
                          {row.status === 'valid' && <Badge className="bg-green-100 text-green-700 text-[9px] py-0">OK</Badge>}
                          {row.status === 'error' && <Badge className="bg-red-100 text-red-700 text-[9px] py-0">{fr ? 'Erreur' : 'Error'}</Badge>}
                          {row.status === 'duplicate' && <Badge className="bg-amber-100 text-amber-700 text-[9px] py-0">{fr ? 'Doublon' : 'Dup'}</Badge>}
                        </td>
                        <td className="p-1.5 font-mono">{row.code}</td>
                        <td className="p-1.5">{row.firstName}</td>
                        <td className="p-1.5">{row.lastName}</td>
                        <td className="p-1.5 text-slate-500">{row.jobTitle}</td>
                        <td className="p-1.5">
                          <span className={row.siteResolved ? 'text-slate-700 dark:text-slate-300' : 'text-amber-600'}>{row.siteName || '-'}</span>
                        </td>
                        <td className="p-1.5">
                          <span className={row.zoneResolved ? 'text-slate-700 dark:text-slate-300' : 'text-amber-600'}>{row.zoneName || '-'}</span>
                        </td>
                        <td className="p-1.5">
                          <span className={row.shiftResolved ? 'text-slate-700 dark:text-slate-300' : 'text-amber-600'}>{row.shiftName || '-'}</span>
                        </td>
                        <td className="p-1.5">
                          {row.errors.map((e, i) => <span key={i} className="text-red-600 text-[10px] block">{e}</span>)}
                          {row.warnings.map((w, i) => <span key={i} className="text-amber-600 text-[10px] block">{w}</span>)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {importPreview.validCount > 0 && (
                <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900 rounded-md p-2.5">
                  <p className="text-xs text-green-700 dark:text-green-400 flex items-start gap-2">
                    <CheckCircle size={14} className="shrink-0 mt-0.5" />
                    {fr
                      ? `${importPreview.validCount} employé(s) seront créés. Les doublons et erreurs seront ignorés.`
                      : `${importPreview.validCount} employee(s) will be created. Duplicates and errors will be skipped.`}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Result */}
          {importStep === 'result' && importResult && (
            <div className="py-3 space-y-3">
              <div className="text-center py-4">
                <CheckCircle className="mx-auto text-green-500 mb-3" size={40} />
                <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                  {fr ? 'Import terminé' : 'Import complete'}
                </h3>
              </div>
              <div className="flex justify-center gap-3">
                <Badge className="bg-green-100 text-green-700 py-1 px-3">{importResult.created} {fr ? 'créés' : 'created'}</Badge>
                <Badge className="bg-amber-100 text-amber-700 py-1 px-3">{importResult.skipped} {fr ? 'ignorés' : 'skipped'}</Badge>
              </div>
              {importResult.errors?.length > 0 && (
                <div className="bg-red-50 dark:bg-red-950/20 rounded p-2 max-h-32 overflow-y-auto">
                  {importResult.errors.map((err, i) => (
                    <p key={i} className="text-xs text-red-600 dark:text-red-400">{err}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {importStep === 'upload' && (
              <>
                <Button variant="outline" onClick={() => setImportDialogOpen(false)} className="h-9">{fr ? 'Annuler' : 'Cancel'}</Button>
                <Button onClick={handlePreview} disabled={!importFile || previewLoading} className="h-9 gap-1.5" data-testid="preview-import-btn">
                  {previewLoading ? <RefreshCw size={14} className="animate-spin" /> : <Eye size={14} />}
                  {fr ? 'Prévisualiser' : 'Preview'}
                </Button>
              </>
            )}
            {importStep === 'preview' && (
              <>
                <Button variant="outline" onClick={() => setImportStep('upload')} className="h-9">{fr ? 'Retour' : 'Back'}</Button>
                <Button onClick={handleImport} disabled={importing || !importPreview?.validCount} className="h-9 gap-1.5" data-testid="confirm-import-btn">
                  {importing ? <RefreshCw size={14} className="animate-spin" /> : <Upload size={14} />}
                  {fr ? `Importer ${importPreview?.validCount || 0} employé(s)` : `Import ${importPreview?.validCount || 0} employee(s)`}
                </Button>
              </>
            )}
            {importStep === 'result' && (
              <Button onClick={() => { setImportDialogOpen(false); resetImportDialog(); }} className="h-9" data-testid="close-import-btn">
                {fr ? 'Fermer' : 'Close'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== EMPLOYEE DETAIL SHEET ========== */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" side="right">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <User size={18} />
              {detailEmployee?.firstName} {detailEmployee?.lastName}
            </SheetTitle>
            <SheetDescription className="text-xs font-mono">{detailEmployee?.employeeCode}</SheetDescription>
          </SheetHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center h-40"><RefreshCw className="animate-spin text-primary" size={24} /></div>
          ) : detailStats ? (
            <div className="mt-4 space-y-4">
              {/* Info Card */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/40 border dark:border-slate-700">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wide">{fr ? 'Poste' : 'Position'}</p>
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{detailStats.employee?.jobTitle || '-'}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/40 border dark:border-slate-700">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wide">{fr ? 'Statut' : 'Status'}</p>
                  <p className="text-sm">{getStatusBadge(detailStats.employee?.currentStatus, detailStats.employee?.active)}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/40 border dark:border-slate-700">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wide">{fr ? 'Zone' : 'Zone'}</p>
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{detailStats.zone?.name || '-'}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/40 border dark:border-slate-700">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wide">{fr ? 'Quart' : 'Shift'}</p>
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{detailStats.shift?.name || '-'}</p>
                </div>
              </div>

              {/* Summary Stats */}
              <Tabs defaultValue="summary" className="w-full">
                <TabsList className="w-full">
                  <TabsTrigger value="summary" className="flex-1 text-xs" data-testid="tab-summary">
                    <BarChart3 size={12} className="mr-1" /> {fr ? 'Résumé' : 'Summary'}
                  </TabsTrigger>
                  <TabsTrigger value="punches" className="flex-1 text-xs" data-testid="tab-punches">
                    <Clock size={12} className="mr-1" /> {fr ? 'Pointages' : 'Punches'}
                  </TabsTrigger>
                  <TabsTrigger value="history" className="flex-1 text-xs" data-testid="tab-history">
                    <Calendar size={12} className="mr-1" /> {fr ? 'Historique' : 'History'}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="summary" className="mt-3 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg border dark:border-slate-700 bg-white dark:bg-slate-800/40">
                      <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">{fr ? 'Jours présent' : 'Days Present'}</p>
                      <p className="text-xl font-bold text-green-600 dark:text-green-400">{detailStats.summary?.daysPresent || 0}</p>
                      {(detailStats.summary?.totalDays || 0) > 0 && <Progress value={((detailStats.summary?.daysPresent || 0) / detailStats.summary.totalDays) * 100} className="mt-1.5 h-1" />}
                    </div>
                    <div className="p-3 rounded-lg border dark:border-slate-700 bg-white dark:bg-slate-800/40">
                      <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">{fr ? 'Jours absent' : 'Days Absent'}</p>
                      <p className="text-xl font-bold text-red-600 dark:text-red-400">{detailStats.summary?.daysAbsent || 0}</p>
                      {(detailStats.summary?.totalDays || 0) > 0 && <Progress value={((detailStats.summary?.daysAbsent || 0) / detailStats.summary.totalDays) * 100} className="mt-1.5 h-1" />}
                    </div>
                    <div className="p-3 rounded-lg border dark:border-slate-700 bg-white dark:bg-slate-800/40">
                      <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">{fr ? 'Heures travaillées' : 'Hours Worked'}</p>
                      <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{Math.round((detailStats.summary?.totalWorkedMinutes || 0) / 60 * 10) / 10}h</p>
                    </div>
                    <div className="p-3 rounded-lg border dark:border-slate-700 bg-white dark:bg-slate-800/40">
                      <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">{fr ? 'Retards' : 'Late'}</p>
                      <p className="text-xl font-bold text-amber-600 dark:text-amber-400">{detailStats.summary?.totalLateIncidents || 0}</p>
                    </div>
                  </div>
                  <div className="p-3 rounded-lg border dark:border-slate-700 bg-white dark:bg-slate-800/40">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">{fr ? 'Heures sup.' : 'Overtime'}</p>
                    <p className="text-xl font-bold text-purple-600 dark:text-purple-400">
                      {Math.round((detailStats.summary?.totalOvertimeMinutes || 0) / 60 * 10) / 10}h
                    </p>
                  </div>
                </TabsContent>

                <TabsContent value="punches" className="mt-3">
                  <div className="space-y-1 max-h-80 overflow-y-auto">
                    {(!detailStats.recentPunches || detailStats.recentPunches.length === 0) ? (
                      <p className="text-center text-sm text-slate-400 py-8">{fr ? 'Aucun pointage récent' : 'No recent punches'}</p>
                    ) : (
                      detailStats.recentPunches.map((p, i) => (
                        <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/40 text-sm border-b dark:border-slate-700/50 last:border-0">
                          <div className={`w-2 h-2 rounded-full ${
                            p.eventType === 'IN' ? 'bg-green-500' :
                            p.eventType === 'OUT' ? 'bg-red-500' :
                            p.eventType === 'BREAK_START' ? 'bg-amber-500' : 'bg-blue-500'
                          }`} />
                          <span className="text-xs text-slate-500 dark:text-slate-400 w-28 font-mono">{p.timestamp?.replace('T', ' ').slice(0, 16)}</span>
                          <Badge className={`text-[10px] ${
                            p.eventType === 'IN' ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400' :
                            p.eventType === 'OUT' ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400' :
                            'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400'
                          }`}>{p.eventType}</Badge>
                        </div>
                      ))
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="history" className="mt-3">
                  <div className="space-y-1 max-h-80 overflow-y-auto">
                    {(!detailStats.dailyStats || detailStats.dailyStats.length === 0) ? (
                      <p className="text-center text-sm text-slate-400 py-8">{fr ? 'Aucun historique' : 'No history'}</p>
                    ) : (
                      detailStats.dailyStats.map((d, i) => (
                        <div key={i} className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/40 text-sm border-b dark:border-slate-700/50 last:border-0">
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${d.status === 'present' ? 'bg-green-500' : 'bg-red-500'}`} />
                            <span className="font-mono text-xs text-slate-500 dark:text-slate-400">{d.date}</span>
                          </div>
                          <div className="flex items-center gap-3 text-xs">
                            <span className="text-slate-600 dark:text-slate-400">{Math.round(d.regularMinutes / 60 * 10) / 10}h</span>
                            {d.overtimeMinutes > 0 && <span className="text-purple-600 dark:text-purple-400">+{Math.round(d.overtimeMinutes / 60 * 10) / 10}h</span>}
                            {d.lateMinutes > 0 && <Badge className="bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 text-[10px]">{d.lateMinutes}min</Badge>}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          ) : (
            <p className="text-center text-sm text-slate-400 py-8">{fr ? 'Aucune donnée' : 'No data'}</p>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
